export interface AudioBufferLike {
  readonly duration: number;
}

export interface AudioBufferSourceLike {
  buffer: AudioBufferLike | null;
  loop: boolean;
  onended: (() => void) | null;
  connect(destination: unknown): void;
  start(): void;
  stop(): void;
  disconnect(): void;
}

export interface GainNodeLike {
  gain: { value: number };
  connect(destination: unknown): void;
  disconnect(): void;
}

export interface AudioContextLike {
  state: "suspended" | "running" | "interrupted" | "closed";
  readonly destination: unknown;
  resume(): Promise<void>;
  decodeAudioData(data: ArrayBuffer): Promise<AudioBufferLike>;
  createBufferSource(): AudioBufferSourceLike;
  createGain(): GainNodeLike;
}

export interface AudioPreferences {
  enabled: boolean;
  masterVolume: number;
}

type AudioContextConstructor = new () => AudioContextLike;

export interface BrowserAudioScope {
  readonly AudioContext?: AudioContextConstructor;
  readonly webkitAudioContext?: AudioContextConstructor;
}

/** Uses the standard API first and the legacy WebKit constructor on Tesla-era WebViews. */
export function createBrowserAudioContext(
  browser: BrowserAudioScope = globalThis as unknown as BrowserAudioScope,
): AudioContextLike {
  const Constructor = (browser.AudioContext as unknown as AudioContextConstructor | undefined) ?? browser.webkitAudioContext;
  if (!Constructor) throw new Error("Web Audio is unavailable");
  return new Constructor();
}

interface ActiveSource {
  source: AudioBufferSourceLike;
  gain: GainNodeLike;
  voice?: AudioVoice;
}

export interface AudioVoice {
  setGain(gain: number): void;
  stop(): void;
}

const MAX_IDENTICAL_EFFECTS = 4;

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export class AudioScope {
  private readonly active = new Map<string, Set<ActiveSource>>();
  private readonly loops = new Map<string, ActiveSource>();
  private destroyed = false;

  constructor(private readonly manager: AudioManager) {}

  decode(effectId: string, data: ArrayBuffer): Promise<AudioBufferLike> {
    if (this.destroyed) return Promise.reject(new Error("Audio scope destroyed"));
    return this.manager.decode(effectId, data);
  }

  play(effectId: string, buffer: AudioBufferLike, gain = 1): boolean {
    if (this.destroyed || !this.manager.preferences.enabled) return false;
    const context = this.manager.runningContext;
    if (!context) return false;

    let sources = this.active.get(effectId);
    if (!sources) {
      sources = new Set<ActiveSource>();
      this.active.set(effectId, sources);
    }
    if (sources.size >= MAX_IDENTICAL_EFFECTS) return false;

    const source = context.createBufferSource();
    const gainNode = context.createGain();
    const record: ActiveSource = { source, gain: gainNode };
    source.buffer = buffer;
    gainNode.gain.value = clampUnit(gain) * this.manager.preferences.masterVolume;
    source.connect(gainNode);
    gainNode.connect(context.destination);

    const cleanup = (): void => {
      sources?.delete(record);
      if (sources?.size === 0) this.active.delete(effectId);
      try { source.disconnect(); } catch { /* already disconnected */ }
      try { gainNode.disconnect(); } catch { /* already disconnected */ }
    };
    source.onended = cleanup;
    sources.add(record);
    source.start();
    return true;
  }

  /**
   * A loop remains owned by this game scope. Repeated calls update the one
   * existing voice instead of stacking periodic one-shot clips forever.
   */
  startLoop(effectId: string, buffer: AudioBufferLike, gain = 1): AudioVoice | null {
    if (this.destroyed || !this.manager.preferences.enabled) return null;
    const context = this.manager.runningContext;
    if (!context) return null;

    const existing = this.loops.get(effectId);
    if (existing) {
      existing.gain.gain.value = clampUnit(gain) * this.manager.preferences.masterVolume;
      return this.loopVoice(effectId, existing);
    }

    const source = context.createBufferSource();
    const gainNode = context.createGain();
    const record: ActiveSource = { source, gain: gainNode };
    source.buffer = buffer;
    source.loop = true;
    gainNode.gain.value = clampUnit(gain) * this.manager.preferences.masterVolume;
    source.connect(gainNode);
    gainNode.connect(context.destination);
    this.loops.set(effectId, record);
    source.start();
    return this.loopVoice(effectId, record);
  }

  private loopVoice(effectId: string, record: ActiveSource): AudioVoice {
    record.voice ??= {
      setGain: (gain: number): void => {
        if (this.destroyed || this.loops.get(effectId) !== record) return;
        record.gain.gain.value = clampUnit(gain) * this.manager.preferences.masterVolume;
      },
      stop: (): void => this.stopLoop(effectId, record),
    };
    return record.voice;
  }

  private stopLoop(effectId: string, record: ActiveSource): void {
    if (this.loops.get(effectId) !== record) return;
    this.loops.delete(effectId);
    record.source.onended = null;
    try { record.source.stop(); } catch { /* the browser may already have stopped it */ }
    try { record.source.disconnect(); } catch { /* already disconnected */ }
    try { record.gain.disconnect(); } catch { /* already disconnected */ }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const [effectId, record] of this.loops) this.stopLoop(effectId, record);
    for (const sources of this.active.values()) {
      for (const { source, gain } of sources) {
        source.onended = null;
        try { source.stop(); } catch { /* source may already be ended */ }
        try { source.disconnect(); } catch { /* already disconnected */ }
        try { gain.disconnect(); } catch { /* already disconnected */ }
      }
    }
    this.active.clear();
  }
}

export class AudioManager {
  private context: AudioContextLike | null = null;
  private readonly decoded = new Map<string, Promise<AudioBufferLike>>();
  private currentPreferences: AudioPreferences = { enabled: true, masterVolume: 1 };
  private unlockInFlight: Promise<boolean> | null = null;

  constructor(
    private readonly factory: () => AudioContextLike = () =>
      createBrowserAudioContext(),
  ) {}

  get preferences(): Readonly<AudioPreferences> {
    return this.currentPreferences;
  }

  get runningContext(): AudioContextLike | null {
    return this.context?.state === "running" ? this.context : null;
  }

  async unlockFromGesture(): Promise<boolean> {
    if (this.unlockInFlight) return this.unlockInFlight;
    this.unlockInFlight = (async () => {
      try {
        if (this.context?.state === "closed") this.context = null;
        this.context ??= this.factory();
        if (this.context.state !== "running") await this.context.resume();
        return this.context.state === "running";
      } catch {
        return false;
      } finally {
        this.unlockInFlight = null;
      }
    })();
    return this.unlockInFlight;
  }

  /**
   * Browsers may reject the first Web Audio unlock while a route is changing.
   * Listen to trusted input for the life of the app so any real interaction can
   * retry it; that includes older Tesla touch events with no PointerEvent API.
   */
  bindGestureRetries(target: EventTarget): () => void {
    const retry = (): void => { void this.unlockFromGesture(); };
    const types = ["pointerdown", "pointerup", "touchend", "click", "keydown"] as const;
    for (const type of types) target.addEventListener(type, retry, { capture: true, passive: true });
    return () => {
      for (const type of types) target.removeEventListener(type, retry, { capture: true });
    };
  }

  async decode(key: string, data: ArrayBuffer): Promise<AudioBufferLike> {
    const existing = this.decoded.get(key);
    if (existing) return existing;
    if (!this.context) throw new Error("Audio context unavailable");

    const decoded = this.context.decodeAudioData(data.slice(0));
    this.decoded.set(key, decoded);
    try {
      return await decoded;
    } catch (error) {
      this.decoded.delete(key);
      throw error;
    }
  }

  createScope(): AudioScope {
    return new AudioScope(this);
  }

  setPreferences(preferences: AudioPreferences): void {
    this.currentPreferences = {
      enabled: preferences.enabled,
      masterVolume: clampUnit(preferences.masterVolume),
    };
  }
}
